// Accessible modal dialogs and a promise-based confirm() that can be used from anywhere.
import { useEffect, useId, useRef, useSyncExternalStore, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"], [contenteditable="plaintext-only"]';

let openModals = 0;

/** True while any modal dialog is shown (keyboard shortcuts should then stay inactive). */
export function isModalOpen(): boolean {
  return openModals > 0;
}

interface ModalProps {
  title: string;
  /** Escape / backdrop. Omit to make the dialog require an explicit choice. */
  onClose?: () => void;
  children: ReactNode;
  /** Buttons row. */
  actions?: ReactNode;
  role?: 'dialog' | 'alertdialog';
  className?: string;
}

/** Counts one open modal layer (shortcuts off, page scrolling locked); returns the release. */
function acquireModalLayer(): () => void {
  openModals++;
  document.body.classList.add('has-modal');
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openModals = Math.max(0, openModals - 1);
    if (openModals === 0) document.body.classList.remove('has-modal');
  };
}

/** Makes a custom full-screen layer (e.g. the login overlay) count as an open modal while `active`. */
export function useModalLayer(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    return acquireModalLayer();
  }, [active]);
}

export function Modal({ title, onClose, children, actions, role = 'dialog', className }: ModalProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const release = acquireModalLayer();
    const el = ref.current;
    const preferred = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>(FOCUSABLE);
    (preferred ?? el)?.focus();
    return () => {
      release();
      // Return focus to where the user was, if that element still exists.
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && onCloseRef.current) {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    // Keep keyboard focus inside the dialog.
    const focusable = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === document.activeElement);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current?.();
      }}
    >
      <div
        ref={ref}
        className={`modal${className ? ` ${className}` : ''}`}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 id={titleId} className="modal__title">
          {title}
        </h2>
        <div className="modal__body">{children}</div>
        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ---------- confirm ----------

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive action: the confirm button is red. */
  danger?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

let queue: PendingConfirm[] = [];
let nextConfirmId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

/** Shows a confirmation dialog; resolves true when the user confirms. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    queue = [...queue, { ...opts, id: nextConfirmId++, resolve }];
    emit();
  });
}

function settle(id: number, ok: boolean): void {
  const item = queue.find((q) => q.id === id);
  if (!item) return;
  queue = queue.filter((q) => q.id !== id);
  emit();
  item.resolve(ok);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getQueue = () => queue;

export function DialogHost() {
  const current = useSyncExternalStore(subscribe, getQueue, getQueue)[0];
  if (!current) return null;
  return (
    <Modal
      key={current.id}
      title={current.title}
      role="alertdialog"
      onClose={() => settle(current.id, false)}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={() => settle(current.id, false)}>
            {current.cancelLabel ?? 'Prekliči'}
          </button>
          <button
            type="button"
            className={`btn ${current.danger ? 'btn--danger' : 'btn--primary'}`}
            data-autofocus={current.danger ? undefined : true}
            onClick={() => settle(current.id, true)}
          >
            {current.confirmLabel ?? 'V redu'}
          </button>
        </>
      }
    >
      {typeof current.message === 'string' ? <p>{current.message}</p> : current.message}
    </Modal>
  );
}
